import { useState } from "react";
import axios from "axios";
import { Send, Mail, Phone, MapPin } from "lucide-react";
import { toast } from "sonner";
import { OWNER } from "../data/catalog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message)
      return toast.error("Merci de remplir tous les champs requis");
    setSubmitting(true);
    try {
      await axios.post(`${API}/contacts`, form);
      setSent(true);
      toast.success("Message envoyé !");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="max-w-7xl mx-auto px-6 py-20">
      <div className="text-xs tracking-[0.3em] uppercase text-white/40">04 — Contact</div>
      <h1 className="font-heading text-5xl sm:text-6xl mt-3 tracking-tighter font-light max-w-3xl">
        Un service sur-mesure ?<br />
        <span className="text-white/50">Parlons-en.</span>
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 mt-16">
        <div className="lg:col-span-2 space-y-8">
          <p className="text-white/60 leading-relaxed">
            Vous avez un besoin spécifique qui ne figure pas parmi les services proposés ? Décrivez-le ici,
            je vous réponds sous 24 heures ouvrées avec une proposition adaptée.
          </p>
          <div className="space-y-4 border-t border-[#1E2D4A] pt-8">
            <a href={`mailto:${OWNER.email}`} className="flex items-start gap-4 group">
              <Mail className="w-5 h-5 mt-0.5 text-white/60 group-hover:text-white" strokeWidth={1.5} />
              <div>
                <div className="text-xs tracking-[0.2em] uppercase text-white/40">E-mail</div>
                <div className="text-white group-hover:underline">{OWNER.email}</div>
              </div>
            </a>
            <a href={`tel:${OWNER.phone}`} className="flex items-start gap-4 group">
              <Phone className="w-5 h-5 mt-0.5 text-white/60 group-hover:text-white" strokeWidth={1.5} />
              <div>
                <div className="text-xs tracking-[0.2em] uppercase text-white/40">Téléphone</div>
                <div className="text-white group-hover:underline">{OWNER.phone}</div>
              </div>
            </a>
            <div className="flex items-start gap-4">
              <MapPin className="w-5 h-5 mt-0.5 text-white/60" strokeWidth={1.5} />
              <div>
                <div className="text-xs tracking-[0.2em] uppercase text-white/40">Zone d'intervention</div>
                <div className="text-white">{OWNER.coverage}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-3">
          {sent ? (
            <div data-testid="contact-success" className="border border-[#1E2D4A] p-10 bg-[#0A1326]/50">
              <h3 className="font-heading text-3xl tracking-tight">Message reçu.</h3>
              <p className="text-white/60 mt-3">Merci {form.name}, je reviens vers vous rapidement.</p>
              <button
                onClick={() => { setSent(false); setForm({ name: "", email: "", phone: "", subject: "", message: "" }); }}
                data-testid="new-message-btn"
                className="mt-8 border border-white/30 px-6 py-3 text-sm hover:border-white"
              >Envoyer un autre message</button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="border border-[#1E2D4A] p-6 sm:p-8 bg-[#0A1326]/50 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-xs text-white/60 mb-1.5">Nom *</label>
                  <input name="name" value={form.name} onChange={change} required data-testid="contact-name"
                    className="w-full bg-[#040914] border border-[#1E2D4A] px-3 py-2.5 text-sm focus:border-white focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs text-white/60 mb-1.5">E-mail *</label>
                  <input type="email" name="email" value={form.email} onChange={change} required data-testid="contact-email"
                    className="w-full bg-[#040914] border border-[#1E2D4A] px-3 py-2.5 text-sm focus:border-white focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs text-white/60 mb-1.5">Téléphone</label>
                  <input name="phone" value={form.phone} onChange={change} data-testid="contact-phone"
                    className="w-full bg-[#040914] border border-[#1E2D4A] px-3 py-2.5 text-sm focus:border-white focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs text-white/60 mb-1.5">Sujet *</label>
                  <input name="subject" value={form.subject} onChange={change} required data-testid="contact-subject"
                    className="w-full bg-[#040914] border border-[#1E2D4A] px-3 py-2.5 text-sm focus:border-white focus:outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-white/60 mb-1.5">Message *</label>
                <textarea name="message" value={form.message} onChange={change} required rows={6} data-testid="contact-message"
                  className="w-full bg-[#040914] border border-[#1E2D4A] px-3 py-2.5 text-sm focus:border-white focus:outline-none resize-none" />
              </div>
              <button type="submit" disabled={submitting} data-testid="contact-submit"
                className="w-full sm:w-auto bg-white text-black px-8 py-3 text-sm tracking-wide inline-flex items-center justify-center gap-2 hover:bg-white/85 disabled:opacity-40">
                <Send className="w-4 h-4" strokeWidth={2} />
                {submitting ? "Envoi..." : "Envoyer le message"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
